/**
 * PujcovnaPage — the Vozidla tab of the rental module (`/pujcovna/vozidla`, the second tab after Rezervace): the
 * fleet (vozový park) of the active firma, one row per car in the rental (pujcovna-plan P2-e). A member with
 * `write` enrols cars ("Zařadit vozidlo", a full page) and edits their pobočka / tarif / kategorie in a modal,
 * which is also where a car is taken out of the rental (deliberately not a row button); `read` sees the list,
 * each car's insurances (P2-g), damage map (P2-i) and odometer readings (P2-l). The Správa tab (pobočky,
 * tarify, history) is shown to `pujcovna:admin`.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppRail } from '@/components/AppRail';
import { Badge } from '@/components/Badge';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Pill } from '@/components/Pill';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { SearchInput } from '@/components/SearchInput';
import { Toolband } from '@/components/Toolband';
import { formatKm } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useActiveFirmaId } from '@/modules/skupiny/otherFirma';
import { useFleetQuery, usePobockyQuery, type FleetEntry } from './api';
import { FleetEditModal } from './FleetEditModal';
import { PojisteniModal } from './PojisteniModal';
import { FotkyModal } from './FotkyModal';
import { KontrolyModal } from './KontrolyModal';
import { NajezdModal } from './NajezdModal';
import { PujcovnaSubtabs } from './PujcovnaSubtabs';
import { cenyZobrazeni } from './ceny-helpers';
import { HistorieVozuModal } from './HistorieVozuModal';
import { ZavadyModal } from './ZavadyModal';
import { stavVazbyText } from './helpers';
import { seradFlotilu } from './rezervace-helpers';
import { hledejVozy } from './flotila-hledani';
import { VozidloOdkaz } from './VozidloOdkaz';
import { WEB_CHYBI } from './fleet-section-forms';
import { specChybi, specShrnuti } from './spec-helpers';

function carTitle(e: FleetEntry): string {
  const v = e.vozidlo;
  const name = v ? [v.znacka_nazev, v.model_nazev, v.provedeni].filter(Boolean).join(' ') : '';
  return name || 'Vozidlo';
}

/** What the dialogs and the row's aria label call the car: the plate when there is one, else the VIN. */
function carLabel(e: FleetEntry): string {
  return e.vozidlo?.spz || e.vin;
}

export function PujcovnaPage() {
  const navigate = useNavigate();
  const { can, resolved } = useModuleAccess();
  const pobocky = usePobockyQuery();
  const fleet = useFleetQuery(resolved && can('pujcovna', 'read'));
  const { isPhone } = useBreakpoint();
  const activeFirmaId = useActiveFirmaId();
  const [editing, setEditing] = useState<FleetEntry | null>(null);
  const [insuring, setInsuring] = useState<FleetEntry | null>(null);
  const [damaged, setDamaged] = useState<FleetEntry | null>(null);
  const [history, setHistory] = useState<FleetEntry | null>(null);
  const [driving, setDriving] = useState<FleetEntry | null>(null);
  const [checking, setChecking] = useState<FleetEntry | null>(null);
  const [photographing, setPhotographing] = useState<FleetEntry | null>(null);
  const canWrite = resolved && can('pujcovna', 'write');

  // by make and model, then plate — the order the calendar uses too (not the order of the fleet entries)
  const rows = useMemo(() => seradFlotilu(fleet.data ?? []), [fleet.data]);
  // the search box filters the whole (small) fleet on the client; the counts of the crown stay the fleet's
  const [hledat, setHledat] = useState('');
  const viditelne = useMemo(() => hledejVozy(rows, hledat), [rows, hledat]);
  const pobockyCount = pobocky.data?.length;
  // Pages render before membership resolves and the fleet query is gated on it, so "no data yet" is
  // `isPending` (a disabled query is pending, not loading) — otherwise the empty state flashes first.
  const fleetPending = !resolved || fleet.isPending;

  const rowActions = (e: FleetEntry) => (
    <span className="flex flex-wrap justify-end gap-2">
      <Button variant="secondary" size="sm" onClick={() => setDamaged(e)}>
        Závady
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setInsuring(e)}>
        Pojištění
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setDriving(e)}>
        Nájezd
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setChecking(e)}>
        Kontroly
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setPhotographing(e)}>
        Fotky
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setHistory(e)}>
        Historie
      </Button>
      {canWrite && (
        <Button variant="secondary" size="sm" onClick={() => setEditing(e)}>
          Upravit
        </Button>
      )}
    </span>
  );

  const columns: DataTableColumn<FleetEntry>[] = [
    {
      id: 'vozidlo',
      header: 'Vozidlo',
      width: 'minmax(220px, 1.6fr)',
      priority: 'primary',
      cell: (e) => (
        <span className="flex min-w-0 flex-col gap-1">
          <span className="truncate text-[14px] text-ink">
            <VozidloOdkaz entry={e}>{carTitle(e)}</VozidloOdkaz>
          </span>
          <span className="flex flex-wrap items-center gap-2">
            {e.vozidlo?.spz && <Pill>{e.vozidlo.spz}</Pill>}
            <span className="font-mono text-[11px] text-ink-3">{e.vin}</span>
          </span>
          {e.stav_vazby !== 'ok' && <span className="text-[12px] text-ink-2">{stavVazbyText(e.stav_vazby)}</span>}
          {e.vozidlo && (
            <span data-testid="fleet-spec" className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              {specShrnuti(e.vozidlo.spec) && <span className="text-[12px] text-ink-2">{specShrnuti(e.vozidlo.spec)}</span>}
              {specChybi(e.vozidlo.spec).length > 0 && (
                <Badge tone="warn" size="sm" className="max-w-full">
                  <span className="truncate">Web: chybí {specChybi(e.vozidlo.spec).join(', ')}</span>
                </Badge>
              )}
            </span>
          )}
        </span>
      ),
    },
    {
      id: 'pobocka',
      header: 'Pobočka · vlastník',
      width: 'minmax(160px, 1fr)',
      priority: 'secondary',
      // The owner (the lessor, whose name, IČO and bank account end up on the contracts) sits under the
      // pobočka as a chip, always: neutral for the active firma's own car, accent for another firma's
      // (the exception a member should notice, as in the stock lists).
      cell: (e) => {
        const owner = e.firma_vlastnika_nazev;
        const other = !!owner && activeFirmaId != null && e.id_firmy_vlastnika !== activeFirmaId;
        return (
          <span className="flex min-w-0 flex-col items-start gap-1">
            <span className="text-[13px] text-ink-2">{e.pobocka_nazev}</span>
            {owner && (
              <Badge tone={other ? 'accent' : 'neutral'} size="sm" className="max-w-full">
                <span
                  className="truncate"
                  data-testid="fleet-vlastnik"
                  data-vlastnik={other ? 'jiny' : 'vlastni'}
                  title={`Vozidlo firmy ${owner}`}
                >
                  {owner}
                </span>
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      id: 'kategorie',
      header: 'Kategorie · tarif',
      width: 'minmax(140px, 1fr)',
      priority: 'secondary',
      cell: (e) => (
        <span className="text-[13px] text-ink-2">
          {[e.kategorie_nazev, e.tarif_nazev].filter(Boolean).join(' · ') || '—'}
        </span>
      ),
    },
    {
      id: 'cena',
      header: 'Cena / den',
      width: 'minmax(110px, 0.8fr)',
      priority: 'secondary',
      cell: (e) => {
        const c = cenyZobrazeni(e);
        return c.ok ? (
          <span className="flex min-w-0 flex-col gap-0.5" data-testid="fleet-cena">
            <span className="font-mono text-[13px] text-ink">{c.hlavni}</span>
            <span className="text-[11px] text-ink-3">{c.detail}</span>
          </span>
        ) : (
          <Badge tone="warn" size="sm" className="max-w-full">
            <span className="truncate" data-testid="fleet-cena-chybi">{c.chyba}</span>
          </Badge>
        );
      },
    },
    {
      id: 'km',
      header: 'Nájezd',
      width: '110px',
      align: 'right',
      priority: 'meta',
      cell: (e) => (
        <span className="font-mono text-[12px] text-ink-2">{formatKm(e.vozidlo?.stav_km) ?? '—'}</span>
      ),
    },
    {
      id: 'stav',
      header: 'Stav',
      width: '120px', // the table's widths are balanced against the actions column (see its clamp): the badges stack instead of widening it
      priority: 'meta',
      // Every row is in the fleet, so "V půjčovně" would say nothing: the column names only what is worth knowing — where the car is offered besides
      // the rental's own reservations, and a stock link that needs a look.
      cell: (e) => (
        <span data-testid="fleet-stav" className="flex min-w-0 flex-col items-start gap-1">
          {e.stav_vazby !== 'ok' && (
            <Badge tone="warn" size="sm" className="whitespace-nowrap">
              Ke kontrole
            </Badge>
          )}
          {/* the module's colours: servis blue (as on the calendar bars), the web green */}
          {e.servis_nahradni && (
            <span title="Nabízí se v servisu jako náhradní vozidlo">
              <Badge tone="accent" size="sm" className="whitespace-nowrap">
                Náhradní vůz
              </Badge>
            </span>
          )}
          {e.verejne && (
            <span className="max-w-full" title={e.web_chybi.length === 0 ? 'Nabízí se veřejně na webu' : `Na web se zatím nedostane: ${e.web_chybi.map((c) => WEB_CHYBI[c]).join(', ')}`}>
              <Badge tone={e.web_chybi.length === 0 ? 'ok' : 'warn'} size="sm" className="max-w-full whitespace-nowrap">
                {e.web_chybi.length === 0 ? 'Na webu' : 'Web: neúplné'}
              </Badge>
            </span>
          )}
        </span>
      ),
    },
    // Phone cards carry no actions column (below): four labelled buttons in the card's auto-sized right
    // column would be wider than the card and squeeze the car out of it.
    ...(isPhone
      ? []
      : [
          {
            id: 'akce',
            header: '',
            // Every row is its own grid, so the width must be the same for all of them (no max-content). It
            // follows the screen: 290 px at the 1281 px desktop breakpoint (what the other columns' minimums
            // leave next to the rail and the gutters, buttons wrap three to a line), growing with the spare
            // width up to 580 px, where the six buttons of a read member and the seven of a writer sit on one line.
            width: 'clamp(290px, calc(100vw - 1000px), 580px)',
            align: 'right',
            priority: 'meta',
            cell: (e: FleetEntry) => rowActions(e),
          } satisfies DataTableColumn<FleetEntry>,
        ]),
  ];

  return (
    <Shell rail={<AppRail active="pujcovna" />}>
      <Crown>
        <Breadcrumb segments={[{ label: 'Půjčovna', href: '/pujcovna' }, { label: 'Vozidla' }]} />
        <Crown.Row className="mt-3">
          <DisplayHeading>
            Vozidla,
            <br />
            <em>vozový park půjčovny.</em>
          </DisplayHeading>
          <CrownMeta
            items={[
              { label: 'Vozidla', value: fleet.data == null ? '—' : String(rows.length) },
              { label: 'Pobočky', value: pobockyCount == null ? '—' : String(pobockyCount) },
            ]}
          />
        </Crown.Row>
        <PujcovnaSubtabs active="vozidla" />
      </Crown>

      <Toolband>
        <SearchInput
          placeholder="Hledat — SPZ, VIN, značka, model, pobočka…"
          aria-label="Hledat vozidlo"
          value={hledat}
          onChange={(e) => setHledat(e.target.value)}
          wrapperClassName="min-w-[220px] max-w-[380px] flex-1"
          data-testid="vozidla-hledat"
        />
        <span data-testid="vozidla-pocet" className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {fleet.isSuccess ? (hledat.trim() ? `${viditelne.length} z ${rows.length} vozidel` : `${rows.length} vozidel`) : '—'}
        </span>
        <Toolband.Spacer />
        {canWrite && (
          <Button
            variant="primary"
            size="sm"
            icon={<Icon name="plus" size={12} />}
            onClick={() => navigate({ to: '/pujcovna/vozidla/nove' })}
          >
            Zařadit vozidlo
          </Button>
        )}
      </Toolband>

      <div data-testid="pujcovna-page" className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
        {fleet.isError ? (
          <EmptyState eyebrow="Chyba" title="Vozidla se nepodařilo načíst" description="Zkuste to prosím znovu za chvíli." />
        ) : fleetPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : rows.length === 0 && pobocky.isError ? (
          // An empty fleet only means "no vehicles" once the pobočky are known: without them we can't tell
          // "no pobočka yet" from "not loaded", and the empty state would point at the wrong next step.
          <EmptyState eyebrow="Chyba" title="Pobočky se nepodařilo načíst" description="Zkuste to prosím znovu za chvíli." />
        ) : rows.length === 0 && pobocky.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : rows.length === 0 ? (
          pobockyCount === 0 ? (
            <EmptyState
              eyebrow="Bez poboček"
              title="Zatím tu není žádná pobočka"
              description="Pobočky půjčovny zakládá správce půjčovny. Jakmile nějaká vznikne, můžete k ní zařadit první vozidlo."
            />
          ) : (
            <EmptyState
              eyebrow="Bez vozidel"
              title="V půjčovně zatím není žádné vozidlo"
              description={
                canWrite
                  ? 'Zařaďte vůz ze skladu tlačítkem „Zařadit vozidlo“ vpravo nahoře nebo z jeho detailu.'
                  : 'Vozidla do půjčovny zařazuje člen s právem měnit data půjčovny.'
              }
            />
          )
        ) : viditelne.length === 0 ? (
          <EmptyState eyebrow="Hledání" title="Hledání nic nenašlo" description="Zkuste jiné slovo — SPZ, VIN, značku, model nebo pobočku." />
        ) : (
          <div data-testid="pujcovna-vozidla">
            <DataTable<FleetEntry>
              aria-label="Vozidla půjčovny"
              variant="bordered"
              columns={columns}
              data={viditelne}
              getRowId={(e) => String(e.id)}
              renderRowDetail={isPhone ? rowActions : undefined}
            />
          </div>
        )}
      </div>

      {editing && (
        <FleetEditModal
          entry={editing}
          title={carLabel(editing)}
          canAdmin={resolved && can('pujcovna', 'admin')}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
      {insuring && (
        <PojisteniModal
          id={insuring.id}
          title={carLabel(insuring)}
          canWrite={canWrite}
          open
          onOpenChange={(open) => !open && setInsuring(null)}
        />
      )}
      {driving && (
        <NajezdModal
          id={driving.id}
          title={carLabel(driving)}
          canWrite={canWrite}
          open
          onOpenChange={(open) => !open && setDriving(null)}
        />
      )}
      {photographing && (
        <FotkyModal
          id={photographing.id}
          title={carLabel(photographing)}
          zdroj={{ typ: photographing.zdroj_typ, id: photographing.id_zdroje }}
          canWrite={canWrite}
          open
          onOpenChange={(open) => !open && setPhotographing(null)}
        />
      )}
      {checking && (
        <KontrolyModal
          id={checking.id}
          title={carLabel(checking)}
          canWrite={canWrite}
          open
          onOpenChange={(open) => !open && setChecking(null)}
        />
      )}
      {damaged && (
        <ZavadyModal
          id={damaged.id}
          title={carLabel(damaged)}
          canWrite={canWrite}
          canAdmin={resolved && can('pujcovna', 'admin')}
          open
          onOpenChange={(open) => !open && setDamaged(null)}
        />
      )}
      {history && (
        <HistorieVozuModal
          id={history.id}
          title={carLabel(history)}
          open
          onOpenChange={(open) => !open && setHistory(null)}
        />
      )}
    </Shell>
  );
}
